import type {
  AnalyticsStreamEvent,
  CreateModelPayload,
  CreateUserPayload,
  LearningFacts,
  LoginInput,
  LyricTrackSummary,
  ModelEndpointSummary,
  ModelTestResult,
  SaveLyricTrackPayload,
  SongVariantsResponse,
  UpdateModelPayload,
  UpdateUserPayload,
  UserSummary
} from "@vocab1600/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: string,
    public readonly fields?: Array<{ path: string; message: string }>
  ) {
    super(detail ?? code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  // Fastify 对"声明了 application/json 却没有请求体"的请求会抛 FST_ERR_CTP_EMPTY_JSON_BODY
  // （错误名 FastifyError）。无 body 的写请求补一个空对象，避免被误判为配置错误。
  const needsBody = method !== "GET" && method !== "HEAD" && init?.body === undefined;
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
    ...(needsBody ? { body: "{}" } : {})
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, body.error ?? "REQUEST_FAILED", body.message, body.fields);
  return body as T;
}

export const api = {
  me: () => request<{ user: UserSummary }>("/api/me"),
  login: (input: LoginInput) =>
    request<{ user: UserSummary }>("/api/auth/login", { method: "POST", body: JSON.stringify(input) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  songVariants: (songNumber: string) => request<SongVariantsResponse>(`/api/catalog/songs/${songNumber}/variants`),

  /* 学情 */
  analytics: () => request<{ period: string; facts: LearningFacts }>("/api/analytics/overview"),
  teacherOverview: () =>
    request<{ studentCount: number; teacherCount: number; facts: LearningFacts }>("/api/teacher/overview"),
  facts: (days = 14, userId?: string) =>
    request<LearningFacts>(`/api/analytics/facts?days=${days}${userId ? `&userId=${userId}` : ""}`),
  students: () =>
    request<{ students: Array<{ id: string; displayName: string; email: string }> }>("/api/teacher/students"),

  /* 用户管理 */
  users: () => request<{ users: UserSummary[] }>("/api/admin/users"),
  createUser: (payload: CreateUserPayload) =>
    request<{ user: UserSummary }>("/api/admin/users", { method: "POST", body: JSON.stringify(payload) }),
  updateUser: (id: string, payload: UpdateUserPayload) =>
    request<{ user: UserSummary }>(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  resetPassword: (id: string, password: string, mustChangePassword = true) =>
    request<{ ok: true; message: string }>(`/api/admin/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password, mustChangePassword })
    }),
  deleteUser: (id: string) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),

  /* 模型管理 */
  models: () => request<{ models: ModelEndpointSummary[] }>("/api/admin/models"),
  createModel: (payload: CreateModelPayload) =>
    request<{ model: ModelEndpointSummary }>("/api/admin/models", { method: "POST", body: JSON.stringify(payload) }),
  updateModel: (id: string, payload: UpdateModelPayload) =>
    request<{ model: ModelEndpointSummary }>(`/api/admin/models/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  deleteModel: (id: string) => request<{ ok: true }>(`/api/admin/models/${id}`, { method: "DELETE" }),
  testModel: (id: string) => request<ModelTestResult>(`/api/admin/models/${id}/test`, { method: "POST" }),

  lyrics: (variantKey: string) => request<LyricTrackSummary>(`/api/lyrics/${variantKey}`),
  lyricsDraft: (variantKey: string) => request<LyricTrackSummary>(`/api/admin/lyrics/${variantKey}`),
  lyricsList: () =>
    request<{ tracks: Array<{ variantKey: string; status: string; lineCount: number }> }>("/api/admin/lyrics"),
  saveLyrics: (variantKey: string, payload: SaveLyricTrackPayload) =>
    request<LyricTrackSummary>(`/api/admin/lyrics/${variantKey}`, { method: "PUT", body: JSON.stringify(payload) }),

  system: () => request<{ version: string; capabilities: Record<string, unknown> }>("/api/admin/system"),

  /* 学习事件上报（失败静默，绝不影响播放体验） */
  sendEvents: (events: unknown[]) =>
    fetch("/api/events", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
      keepalive: true
    }).catch(() => undefined)
};

/**
 * 学情报告的 SSE 流式读取。
 *
 * 用 fetch + ReadableStream 而不是 EventSource，因为 EventSource 只支持 GET，
 * 无法携带 JSON 请求体，也无法自定义请求头。
 */
export async function streamAnalyticsReport(
  payload: { days: number; modelId?: string; scope?: string; targetId?: string },
  onEvent: (event: AnalyticsStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch("/api/analytics/report", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.error ?? "REPORT_FAILED", body.message);
  }
  if (!response.body) throw new Error("服务器未返回流式响应");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of block.split("\n")) {
        if (!line.startsWith("data:")) continue;
        try { onEvent(JSON.parse(line.slice(5).trim()) as AnalyticsStreamEvent); } catch { /* 忽略心跳等非 JSON 行 */ }
      }
    }
  }
}
